import { forwardRef, memo } from "react";
import Moment from "react-moment";
import { formatCurrency, getScrV2VarianceLabel } from "./SCRV2InvoiceDetailHelpers";
import { renderScrV2IdentityDate } from "./SCRV2IdentityPresentation";
import { sumScrV2TransactionCounts } from "./SCRV2SummaryReportComponents";
import {
  ScrV2PrintAmountRow as PrintAmountRow,
  ScrV2PrintAmountRows as PrintAmountRows,
  ScrV2PrintKpi as PrintKpi,
  ScrV2PrintPanel as PrintPanel,
  ScrV2PrintPaymentGroup as PrintPaymentGroup,
  ScrV2PrintPaymentRow as PrintPaymentRow,
} from "./SCRV2SummaryReportPrintComponents";
import { SCR_V2_SUMMARY_PRINT_STYLES } from "./SCRV2SummaryReportPrintStyles";

const SCRV2SummaryReportPrint = forwardRef(({ report }, ref) => {
  if (!report?.scrId) {
    return <div ref={ref} />;
  }

  const printedAt = new Date();
  const highlights = report.highlights || {};
  const paymentDue = report.paymentDue || {};
  const paymentReceived = report.paymentReceived || {};
  const reversals = report.reversals || {};

  const collectionRate = Number(highlights.collectionRatePercent || 0);
  const variance = Number(highlights.varianceAmount || 0);
  const varianceLabel = getScrV2VarianceLabel(variance);

  const vizLinkBreakdown = paymentReceived.vizLinkBreakdown || [];
  const nonVizLinkBreakdown = paymentReceived.nonVizLinkBreakdown || [];
  const recoveryBreakdown = paymentReceived.recoveryBreakdown || [];

  const hasPaymentBreakdown =
    vizLinkBreakdown.length > 0 ||
    nonVizLinkBreakdown.length > 0 ||
    recoveryBreakdown.length > 0 ||
    paymentReceived.nonVizLinkAmountInHand > 0 ||
    paymentReceived.nonVizLinkAmountDepositedInBank > 0 ||
    paymentDue.totalRecoveryAmount > 0;

  return (
    <div ref={ref} className="scr-v2-summary-print-view">
      <style>{SCR_V2_SUMMARY_PRINT_STYLES}</style>

      <div className="scr-v2-print-header">
        <h1 className="scr-v2-print-header__title">SCR Summary Report</h1>
        <div className="scr-v2-print-header__meta">
          <div
            className={`scr-v2-print-status ${report.isFinalized ? "scr-v2-print-status--finalized" : "scr-v2-print-status--open"
              }`}
          >
            {report.isFinalized ? "Finalized" : "Open"}
          </div>
          <div>
            SCR No: <strong>{report.scrNo || "-"}</strong>
          </div>
        </div>
      </div>

      <div className="scr-v2-print-info-strip">
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Distributor</span>
          <span className="scr-v2-print-info-item__value">{report.distributorName || "-"}</span>
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Distribution Center</span>
          <span className="scr-v2-print-info-item__value">{report.distCenterName || "-"}</span>
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">DLM Details</span>
          <span className="scr-v2-print-info-item__value">
            {report.dlmName || report.dlmUserName || "-"}
          </span>
          {report.deliveryQRCode ? (
            <span className="scr-v2-print-info-item__sub">{report.deliveryQRCode}</span>
          ) : null}
        </div>
        <div className="scr-v2-print-info-item">
          <span className="scr-v2-print-info-item__label">Date</span>
          <span className="scr-v2-print-info-item__value">
            {renderScrV2IdentityDate(report.businessDate)}
          </span>
          {report.finalizedDate ? (
            <span className="scr-v2-print-info-item__sub">
              Finalized: {renderScrV2IdentityDate(report.finalizedDate)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="scr-v2-print-kpi-row">
        <PrintKpi
          title="Total Payment Due"
          amount={formatCurrency(paymentDue.grandTotalDue)}
          detail={`${highlights.totalInvoices || 0} invoice(s) total`}
        />
        <PrintKpi
          title="Total Payment Received"
          amount={formatCurrency(highlights.totalReceived)}
          detail={`${collectionRate}% collection rate`}
        />
        <PrintKpi
          title={`${varianceLabel} (Variance)`}
          amount={formatCurrency(Math.abs(variance))}
          detail={variance === 0 ? "Fully balanced" : `${varianceLabel} amount`}
        />
      </div>

      <div className="scr-v2-print-middle">
        <div className="scr-v2-print-left">
          <PrintPanel
            title="Payment Due Breakdown"
          >
            <PrintAmountRows>
              <PrintAmountRow
                label="Total Original Invoice Amount"
                value={paymentDue.totalOriginalInvoicesAmount}
              />
              <PrintAmountRow label="Adjustments" value={paymentDue.totalAdjustmentAmount} signed />
              <PrintAmountRow label="Total Invoice Amount" value={paymentDue.totalInvoicesAmount} />
              <PrintAmountRow
                label="Credit Amount"
                value={paymentDue.totalCreditAmount}
                deduct={paymentDue.totalCreditAmount > 0}
              />
              <PrintAmountRow label="Credit Recovery" value={paymentDue.totalRecoveryAmount} signed />
              <PrintAmountRow
                label="GRAND TOTAL DUE"
                value={paymentDue.grandTotalDue}
                strong
                highlight
              />
            </PrintAmountRows>
          </PrintPanel>
        </div>

        <div className="scr-v2-print-right-stack">
          <div className="scr-v2-print-efficiency">
            <div className="scr-v2-print-efficiency__label">Collection Efficiency</div>
            <div className="scr-v2-print-efficiency__rate">{collectionRate}%</div>
            <div className="scr-v2-print-efficiency__detail">
              {formatCurrency(highlights.totalReceived)} received of{" "}
              {formatCurrency(paymentDue.grandTotalDue)} due
            </div>
          </div>

          <PrintPanel title="Reversals" badge={`${reversals.reversalCount || 0} reversal(s)`}>
            <PrintAmountRows>
              <PrintAmountRow
                label="Total Transaction Reversals"
                value={reversals.totalReversalAmount}
                strong
              />
            </PrintAmountRows>
          </PrintPanel>
        </div>
      </div>

      <PrintPanel title="Payment Received Breakdown">
        <table className="scr-v2-print-table">
          <thead>
            <tr>
              <th>Payment Type</th>
              <th>Count</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {!hasPaymentBreakdown ? (
              <tr>
                <td colSpan="3">No payment breakdown available.</td>
              </tr>
            ) : null}

            {vizLinkBreakdown.length > 0 ? (
              <PrintPaymentGroup
                title="VizLink Collection"
                amount={paymentReceived.totalVizLinkCollectionAmount}
                count={sumScrV2TransactionCounts(vizLinkBreakdown)}
              >
                {vizLinkBreakdown.map((item) => (
                  <PrintPaymentRow
                    key={`viz-${item.paymentType}`}
                    label={item.paymentType}
                    count={item.transactionCount}
                    amount={item.amount}
                    indent
                  />
                ))}
              </PrintPaymentGroup>
            ) : null}

            {nonVizLinkBreakdown.length > 0 ||
              paymentReceived.totalNonVizLinkCollectionAmount > 0 ? (
              <PrintPaymentGroup
                title="Non-VizLink Collection"
                amount={paymentReceived.totalNonVizLinkCollectionAmount}
                count={sumScrV2TransactionCounts(nonVizLinkBreakdown)}
              >
                {nonVizLinkBreakdown.map((item) => (
                  <PrintPaymentRow
                    key={`nv-${item.paymentType}`}
                    label={item.paymentType}
                    count={item.transactionCount}
                    amount={item.amount}
                    indent
                  />
                ))}
              </PrintPaymentGroup>
            ) : null}

            {recoveryBreakdown.length > 0 || paymentDue.totalRecoveryAmount > 0 ? (
              <PrintPaymentGroup
                title="Recovery Collection"
                amount={paymentDue.totalRecoveryAmount}
                count={sumScrV2TransactionCounts(recoveryBreakdown)}
              >
                {recoveryBreakdown.map((item) => (
                  <PrintPaymentRow
                    key={`recovery-${item.paymentType}`}
                    label={item.paymentType}
                    count={item.transactionCount}
                    amount={item.amount}
                    indent
                  />
                ))}
              </PrintPaymentGroup>
            ) : null}

            <PrintPaymentRow
              label="Amount Collected"
              count={paymentReceived.amountCollectedTransactionCount}
              amount={paymentReceived.amountCollected}
            />
            <PrintPaymentRow
              label="Amount Deposited in Bank"
              count={paymentReceived.nonVizLinkDepositedTransactionCount}
              amount={paymentReceived.nonVizLinkAmountDepositedInBank}
            />
            <PrintPaymentRow
              label="Amount in Hand"
              count={paymentReceived.nonVizLinkInHandTransactionCount}
              amount={paymentReceived.nonVizLinkAmountInHand}
            />
          </tbody>
        </table>
      </PrintPanel>

      <div className="scr-v2-print-footer">
        Printed: <Moment format="DD-MM-YYYY HH:mm">{printedAt}</Moment>
      </div>
    </div>
  );
});

SCRV2SummaryReportPrint.displayName = "SCRV2SummaryReportPrint";

export default memo(SCRV2SummaryReportPrint);
